// HtmlVideo.jsx
import React from "react";
import "./HTML_MAIN_CSS.css";

const HtmlVideo = () => {
  return (
    <div className="html-container">
      <h1>HTML – Video</h1>

      {/* Introduction */}
      <section className="html-section">
        <h2>1. Introduction</h2>
        <p>
          HTML5 introduced the <code>&lt;video&gt;</code> element, which allows
          embedding video content directly into a web page without relying on
          plugins like Flash. It provides built-in controls, attributes, and
          flexibility for playing videos across devices and browsers.
        </p>
      </section>

      {/* Syntax */}
      <section className="html-section">
        <h2>2. Basic Syntax</h2>
        <pre className="code-block">
          <code>{`<video width="640" height="360" controls>
  <source src="movie.mp4" type="video/mp4">
  <source src="movie.ogg" type="video/ogg">
  Your browser does not support the video tag.
</video>`}</code>
        </pre>
        <p>
          The <code>&lt;video&gt;</code> tag can contain multiple{" "}
          <code>&lt;source&gt;</code> elements to provide fallback formats for
          different browsers.
        </p>
      </section>

      {/* Example */}
      <section className="html-section">
        <h2>3. Example</h2>
        <video width="480" height="270" controls>
          <source
            src="https://www.w3schools.com/html/mov_bbb.mp4"
            type="video/mp4"
          />
          <source
            src="https://www.w3schools.com/html/mov_bbb.ogg"
            type="video/ogg"
          />
          Your browser does not support the video tag.
        </video>
      </section>

      {/* Attributes */}
      <section className="html-section">
        <h2>4. Video Attributes</h2>
        <p>Below are the most commonly used attributes in the video tag:</p>
        <ul>
          <li>
            <strong>controls:</strong> Displays play, pause, and volume options.
          </li>
          <li>
            <strong>autoplay:</strong> Automatically starts the video when the
            page loads.
          </li>
          <li>
            <strong>loop:</strong> Replays the video automatically after it
            finishes.
          </li>
          <li>
            <strong>muted:</strong> Starts the video with no sound.
          </li>
          <li>
            <strong>poster:</strong> Sets an image to display before playback
            starts.
          </li>
          <li>
            <strong>preload:</strong> Defines how the browser should load the
            video (<code>auto</code>, <code>metadata</code>, or{" "}
            <code>none</code>).
          </li>
          <li>
            <strong>width</strong> &amp; <strong>height:</strong> Define the
            video dimensions.
          </li>
        </ul>
      </section>

      {/* Example with poster */}
      <section className="html-section">
        <h2>5. Example with Poster</h2>
        <pre className="code-block">
          <code>{`<video width="480" height="270" controls poster="thumbnail.jpg">
  <source src="video.mp4" type="video/mp4">
  Your browser does not support the video tag.
</video>`}</code>
        </pre>
        <p>
          The <code>poster</code> attribute shows an image thumbnail before the
          video starts.
        </p>
      </section>

      {/* Supported Formats */}
      <section className="html-section">
        <h2>6. Supported Video Formats</h2>
        <table className="styled-table">
          <thead>
            <tr>
              <th>Format</th>
              <th>File Extension</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>MP4</td>
              <td>.mp4</td>
              <td>video/mp4</td>
            </tr>
            <tr>
              <td>WebM</td>
              <td>.webm</td>
              <td>video/webm</td>
            </tr>
            <tr>
              <td>Ogg</td>
              <td>.ogg</td>
              <td>video/ogg</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Multiple Source Example */}
      <section className="html-section">
        <h2>7. Multiple Source Example</h2>
        <p>
          Adding multiple <code>&lt;source&gt;</code> tags ensures that the
          video plays on all major browsers.
        </p>
        <pre className="code-block">
          <code>{`<video controls>
  <source src="movie.mp4" type="video/mp4">
  <source src="movie.webm" type="video/webm">
  <source src="movie.ogg" type="video/ogg">
  Your browser does not support HTML video.
</video>`}</code>
        </pre>
      </section>

      {/* Embedding YouTube */}
      <section className="html-section">
        <h2>8. Embedding YouTube Videos</h2>
        <p>
          You can also embed external videos using the{" "}
          <code>&lt;iframe&gt;</code> element for platforms like YouTube.
        </p>
        <pre className="code-block">
          <code>{`<iframe width="560" height="315" 
src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
title="YouTube video player" frameborder="0" 
allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
allowfullscreen></iframe>`}</code>
        </pre>

        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/dQw4w9WgXcQ"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </section>

      {/* Browser Support */}
      <section className="html-section">
        <h2>9. Browser Support</h2>
        <p>
          All modern browsers support the <code>&lt;video&gt;</code> element,
          but certain formats may not work on all browsers. It’s recommended to
          use <strong>MP4</strong> for the widest compatibility.
        </p>
      </section>

      {/* Best Practices */}
      <section className="html-section">
        <h2>10. Best Practices</h2>
        <ul>
          <li>Provide multiple formats for cross-browser compatibility.</li>
          <li>Use <code>controls</code> for accessibility.</li>
          <li>
            Keep file sizes optimized to ensure faster loading and better
            performance.
          </li>
          <li>Use a poster image for a better user experience.</li>
          <li>
            Avoid using <code>autoplay</code> with sound — it can annoy users.
          </li>
        </ul>
      </section>

      {/* Problem Statement */}
      <section className="html-section problem">
        <h2>11. Problem Statement</h2>
        <p>
          Many developers previously relied on third-party plugins like Flash
          for embedding videos. However, Flash caused performance and security
          issues. The <code>&lt;video&gt;</code> element solves these problems
          by providing a native, secure, and efficient way to play video content
          directly in browsers.
        </p>
      </section>
    </div>
  );
};

export default HtmlVideo;
